import { Injectable, computed } from '@angular/core';
import { AppStateService } from './app-state.service';
import { Card } from '../models/card.model';
import { CardStatus, QuickFilter } from '../constants/app.constants';
import { UiFilterState } from '../models/app-state.model';

@Injectable({
  providedIn: 'root'
})
export class SelectorsService {
  constructor(private appStateService: AppStateService) {}

  public readonly allCards = computed(() => this.appStateService.state().cards);
  public readonly categories = computed(() => this.appStateService.state().categories);
  public readonly ladderConfig = computed(() => this.appStateService.state().ladder);
  public readonly allCardsFilter = computed(() => this.appStateService.state().ui.allCards);
  public readonly ladderFilter = computed(() => this.appStateService.state().ui.ladder);
  public readonly categoryUsageCounts = computed(() => {
      const usageCounts = new Map<string, number>();

      for (const category of this.categories()) {
          usageCounts.set(category.id, 0);
      }

      for (const card of this.allCards()) {
          usageCounts.set(card.categoryId, (usageCounts.get(card.categoryId) ?? 0) + 1);
      }

      return usageCounts;
  });
  public readonly activeCards = computed(() => {
      return this.allCards().filter(card => card.status === CardStatus.IN_PROGRESS);
  });

  public readonly allCardsSortedNewest = computed(() => {
     return [...this.allCards()].sort((a, b) => 
        new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
     );
  });

  public readonly visibleLadderCards = computed(() => {
     return this.allCards().filter(c => c.showInLadder);
  });

  public readonly filteredAllCards = computed(() => {
     return this.applyFilter(this.allCardsSortedNewest(), this.allCardsFilter());
  });

  public readonly filteredLadderCards = computed(() => {
     return this.applyFilter(this.visibleLadderCards(), this.ladderFilter());
  });

  public readonly ladderLanes = computed(() => {
      const visible = this.filteredLadderCards();
      const levels = this.ladderConfig().levels;
      
      const map = new Map<number, Card[]>();
      
      levels.forEach(level => {
          const inLevel = visible.filter(c => c.priority === level);
          const sorted = inLevel.sort((a,b) => (a.importance || 0) - (b.importance || 0));
          map.set(level, sorted);
      });
      
      return map;
  });

  public getChildren(parentCardId: string): Card[] {
      return this.allCards().filter(c => c.parentCardId === parentCardId);
  }

  public getCardsByLadderLevel(level: number): Card[] {
      return this.ladderLanes().get(level) ?? [];
  }

  public getCategoryUsageCount(categoryId: string): number {
      return this.categoryUsageCounts().get(categoryId) ?? 0;
  }

  public getParent(childCardId: string): Card | undefined {
      const child = this.allCards().find(c => c.id === childCardId);
      if (!child || !child.parentCardId) return undefined;
      return this.allCards().find(c => c.id === child.parentCardId);
  }

  public isFilterActive(view: 'allCards' | 'ladder'): boolean {
      const filter = view === 'ladder' ? this.ladderFilter() : this.allCardsFilter();
      return filter.textQuery.trim() !== ''
          || filter.status !== 'ALL'
          || filter.categoryId !== null
          || filter.quickFilter !== QuickFilter.ALL;
  }

  private applyFilter(cards: Card[], filter: UiFilterState): Card[] {
      return cards.filter(card => {
          if (filter.categoryId && card.categoryId !== filter.categoryId) return false;
          if (filter.status !== 'ALL' && card.status !== filter.status) return false;
          if (filter.quickFilter === QuickFilter.ACTIVE && card.status !== CardStatus.IN_PROGRESS) return false;
          if (filter.quickFilter === QuickFilter.COMPLETED
              && card.status !== CardStatus.DONE
              && card.status !== CardStatus.CLOSED) return false;

          if (filter.textQuery.trim() !== '') {
              const query = filter.textQuery.trim().toLowerCase();
              const inTitle = card.title.toLowerCase().includes(query);
              const inDesc = card.description.toLowerCase().includes(query);
              const inNotes = card.notes.toLowerCase().includes(query);

              if (!inTitle && !inDesc && !inNotes) return false;
          }

          return true;
      });
  }
}
